import { useMemo } from 'react'
import moment from 'moment'
import { regular } from '@fortawesome/fontawesome-svg-core/import.macro'
import { useReselector } from '@/utils/sharedHooks'
import { useNavigate } from 'react-router-dom'

import { Text, Button, Card, Image, CardColors } from '@/ui'
import { TypographyStyles, TextColors } from '@/styles'

import { DeadlineLabel } from '../DeadlineLabel'
import { DATE_FORMATS, DATE_FORMATS_LUXON } from '@/utils/dateHelpers'
import { UserTaxEstimate } from '@/features/taxes/quarterly-tax-estimates/userTaxEstimates.slice'
import { SafeHarborPaymentCard } from './SafeHarborPaymentCard'
import { StateSafeHarborCard } from './StateSafeHarborCard'
import { WhatIsSafeHarborPopup } from './helpers'

import {
  selectActiveQTEDetailsAsDates,
  selectActiveQuarterlyTaxEstimateDetails,
} from '@/featuresV1/Admin/QuarterlyTaxEstimateDetails/quarterlyTaxEstimateDetails.selector'

const FederalSafeHarborCard = () => {
  const navigate = useNavigate()

  const activeQuarterDetails = useReselector(selectActiveQTEDetailsAsDates)

  if (!activeQuarterDetails) {
    return null
  }

  return (
    <Card color={CardColors.LEVEL_1}>
      <div className="flex flex-col lg:flex-row gap-4 items-start">
        {/* Image Column */}
        <div className="w-full lg:w-1/3 flex justify-center lg:justify-start">
          <Image
            src="https://heard-images.s3.amazonaws.com/assets/writing_check.svg"
            alt="Writing Check Illustration"
            className="w-[150px] h-[150px]"
          />
        </div>

        {/* Content Column */}
        <div className="w-full lg:w-2/3 space-y-4">
          <Text variant={TypographyStyles.EYEBROW} color={TextColors.MUTED}>
            important note
          </Text>

          <Text variant={TypographyStyles.H2}>
            Paying Q{activeQuarterDetails.taxQuarter} Federal Quarterly Taxes
          </Text>

          <Text variant={TypographyStyles.BODY_LG} color={TextColors.MUTED}>
            Since you signed up after{' '}
            {activeQuarterDetails.withOffset.newUserCutOff.toFormat(
              DATE_FORMATS_LUXON.DISPLAY_LONG
            )}
            , we won&apos;t be able to provide a calculated quarterly tax
            estimate using your practice income this quarter. This quarter, you
            can use our safe harbor estimate calculator to determine an estimate
            that will help minimize federal underpayment penalties.
          </Text>

          <WhatIsSafeHarborPopup />

          <Button
            onClick={() => {
              navigate('/taxes/quarterly/calculate-safe-harbor')
            }}
          >
            Calculate safe harbor
          </Button>
        </div>
      </div>
    </Card>
  )
}

export const UserJoinedTooLate = ({
  activeQuarterText,
  federalTaxEstimate,
}: {
  activeQuarterText: string
  federalTaxEstimate: UserTaxEstimate | null
}) => {
  const activeQuarterDetails = useReselector(
    selectActiveQuarterlyTaxEstimateDetails
  )

  const federalContent = useMemo(() => {
    if (!activeQuarterDetails || !federalTaxEstimate) {
      return null
    }
    if (
      federalTaxEstimate.estimateInCents >= 0 &&
      federalTaxEstimate.safeHarborType
    ) {
      return <SafeHarborPaymentCard federalTaxEstimate={federalTaxEstimate} />
    } else {
      return <FederalSafeHarborCard />
    }
  }, [federalTaxEstimate, activeQuarterDetails])

  return (
    <div className="space-y-6">
      <Text variant={TypographyStyles.H2} className="flex text-center">
        {activeQuarterText} Estimates
      </Text>

      <div className="p-0">
        <DeadlineLabel
          icon={regular('square-dollar')}
          text="IRS payment due"
          prominentText={moment
            .utc(activeQuarterDetails?.irsPaymentDueAt)
            .format(DATE_FORMATS.DISPLAY_LONG)}
        />
      </div>

      {federalContent}

      <StateSafeHarborCard />
    </div>
  )
}
